// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback } from 'react';

import {
  type Facility,
  type Project,
  type WorkOrder,
  type CreateWorkOrderBody,
  today,
  useEvolvePost,
} from '@evolvemep/api-ts';
import { Button, Flex, Textarea, TextInput, ActionIcon } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useUncontrolled } from '@mantine/hooks';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { DateInputWrapped } from 'components/Mantine/DateInputWrapped';
import { FacilitySelect } from 'components/Mantine/GlobalFilters/FacilitySelect';
import { ProjectSelect } from 'components/Mantine/GlobalFilters/ProjectSelect';
import { FormModal, useFormModal, type FormModalProps } from 'components/Mantine/Modals/FormModal';
import { autofocusIf } from 'components/Mantine/Modals/helpers';
import { useEvolveNavigate } from 'components/Mantine/Navigation/useEvolveNavigate';
import type { WithOptional, WithNullable } from 'helpers/helper-types';
import { isNotNil, isNil } from 'helpers/isNotNil';
import { useIsMobile } from 'hooks/useIsMobile';

export type NewWorkOrderModalProps = {
  /** If not provided, will instead navigate to the newly created work order. */
  onWorkOrderCreated?: (workOrder: WorkOrder) => void;
  defaultProject?: Project;
  /** If true, will prevent the user from selecting a project. Used for creating a work order in a specific project. */
  disableProjectSelection?: boolean;
  defaultName?: string;
  /** If provided, will hide the "New work order" button and directly control the Modal state */
  opened?: boolean;
  onClose?: () => void;
  modalProps?: Partial<FormModalProps>;
};

type ValidatedFormBody = Pick<CreateWorkOrderBody, 'name' | 'description' | 'needByDate'> & {
  project: Pick<Project, 'projectId' | 'projectName'>;
  facility: Pick<Facility, 'facilityId' | 'facilityName'>;
};
type FormBody = WithNullable<ValidatedFormBody>;

const NewWorkOrderForm = ({
  onWorkOrderCreated,
  defaultProject,
  disableProjectSelection,
  defaultName,
  modalProps,
}: NewWorkOrderModalProps) => {
  const navigate = useEvolveNavigate('Work Orders');

  const form = useForm<FormBody>({
    initialValues: {
      project: defaultProject ?? null,
      facility: isNotNil(defaultProject?.defaultFacilityId)
        ? {
            facilityId: defaultProject.defaultFacilityId,
            facilityName: defaultProject.defaultFacilityName ?? '',
          }
        : null,
      name: defaultName ?? '',
      description: '',
      needByDate: null,
    },
    validate: {
      facility: (v) => !v,
      project: (v) => !v,
      name: (v) => !v,
    },
  });

  const { apiCall: createWorkOrder } = useEvolvePost('shop/workOrder');

  const onSubmit = async (rawValues: typeof form.values) => {
    const { project, facility, needByDate, ...values } = rawValues as ValidatedFormBody;
    await createWorkOrder({
      projectId: project.projectId,
      facilityId: facility.facilityId,
      needByDate: needByDate ?? undefined,
      ...values,
    }).then((newWorkOrder) => {
      if (onWorkOrderCreated) {
        onWorkOrderCreated(newWorkOrder);
      } else {
        void navigate(`/work-orders/${newWorkOrder.workOrderId}`);
      }
    });
  };

  useFormModal(form, onSubmit);

  return (
    <>
      <Flex gap="xs" wrap="wrap">
        <ProjectSelect
          label="Project"
          required
          autoSelectWhenOnlyOneOption
          disabled={disableProjectSelection}
          {...form.getInputProps('project')}
          onChange={(p) => {
            form.setFieldValue('project', p);
            if (isNotNil(p?.defaultFacilityId)) {
              // Pre-fill facility from the project's default
              form.setFieldValue('facility', {
                facilityId: p.defaultFacilityId,
                facilityName: p.defaultFacilityName ?? '',
              });
            }
          }}
          flex={1}
        />
        <FacilitySelect
          label="Facility"
          required
          autoSelectWhenOnlyOneOption
          {...form.getInputProps('facility')}
          flex={1}
        />
      </Flex>
      <TextInput
        label="Work Order Name"
        w="100%"
        maxLength={128}
        withAsterisk
        {...autofocusIf(isNotNil(defaultProject?.defaultFacilityId))}
        {...form.getInputProps('name')}
      />
      <DateInputWrapped
        w="100%"
        label="Need By Date"
        minDate={today()}
        clearable
        popoverProps={{
          zIndex: modalProps?.zIndex,
        }}
        {...form.getInputProps('needByDate')}
      />
      <Textarea label="Description" maxLength={1024} minRows={6} autosize {...form.getInputProps('description')} />
    </>
  );
};

export const NewWorkOrderModal = ({
  opened: parentOpened,
  onClose: parentOnClose,
  ...props
}: WithOptional<NewWorkOrderModalProps, 'onClose'>) => {
  const isMobile = useIsMobile();
  const [opened, setOpened] = useUncontrolled({
    value: parentOpened,
    defaultValue: false,
    onChange: (o) => {
      if (!o) parentOnClose?.();
    },
  });
  const onClose = useCallback(() => setOpened(false), [setOpened]);
  return (
    <>
      {isNil(parentOpened) &&
        (isMobile ? (
          <ActionIcon onClick={() => setOpened(true)} variant="filled" size="input-sm">
            <EvolveIcon icon="Add" />
          </ActionIcon>
        ) : (
          <Button leftSection={<EvolveIcon icon="Add" size="sm" />} onClick={() => setOpened(true)}>
            New work order
          </Button>
        ))}
      <FormModal
        title="New work order"
        {...props.modalProps}
        opened={opened}
        onClose={onClose}
        size="md"
        confirmationText="Create"
        type="submit"
      >
        <NewWorkOrderForm {...props} onClose={onClose} />
      </FormModal>
    </>
  );
};
